import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { addDays, addWeeks, format, startOfWeek, isToday } from "date-fns";
import { it } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";

const ymd = (d) => format(d, "yyyy-MM-dd");
const CELLA = {
  in_corso: "bg-sky-100 text-sky-900 ring-sky-300 hover:bg-sky-200",
  prenotato: "bg-amber-100 text-amber-900 ring-amber-300 hover:bg-amber-200",
  restituito: "bg-slate-100 text-slate-500 ring-slate-200 hover:bg-slate-200",
};
const LEGENDA = [["Libera", "bg-emerald-50 ring-emerald-200"], ["Prenotata", "bg-amber-100 ring-amber-300"], ["Noleggiata", "bg-sky-100 ring-sky-300"], ["Restituita", "bg-slate-100 ring-slate-200"], ["Manutenzione / fuori servizio", "bg-slate-200 ring-slate-300"]];

const Cella = ({ bike, giorno, noleggi, onApri, onNuovo }) => {
  const d = ymd(giorno);
  const qui = noleggi.filter((n) => n.bici_ids.includes(bike.id) && n.inizio < `${d}T23:59` && n.fine > `${d}T00:00`);
  if (qui.length) {
    return (
      <div className="space-y-1">
        {qui.map((n) => (
          <button key={n.id} onClick={() => onApri(n.id)} data-testid={`cal-cella-${bike.id}-${d}`}
            className={`block w-full truncate rounded px-1.5 py-1 text-left text-[11px] font-semibold ring-1 transition-colors ${CELLA[n.stato]}`}>
            {n.cliente_nome}<span className="block font-mono text-[10px] font-normal opacity-70">{n.inizio.slice(0, 10) === d ? `dalle ${n.inizio.slice(11)}` : n.fine.slice(0, 10) === d ? `fino ${n.fine.slice(11)}` : "tutto il giorno"}</span>
          </button>
        ))}
      </div>
    );
  }
  if (bike.stato_flotta !== "disponibile") {
    return <div className="rounded bg-slate-200 px-1.5 py-2 text-center text-[11px] text-slate-500 ring-1 ring-slate-300" data-testid={`cal-cella-${bike.id}-${d}`}>{bike.stato_flotta === "manutenzione" ? "Manutenzione" : "Fuori servizio"}</div>;
  }
  return (
    <button onClick={() => onNuovo(bike.id, d)} data-testid={`cal-cella-${bike.id}-${d}`} title="Crea noleggio"
      className="block w-full rounded bg-emerald-50 px-1.5 py-2 text-center text-[11px] font-semibold text-emerald-700 ring-1 ring-emerald-200 transition-colors hover:bg-emerald-100">Libera</button>
  );
};

export default function CalendarioFlotta() {
  const navigate = useNavigate();
  const [inizio, setInizio] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [flotta, setFlotta] = useState([]);
  const [noleggi, setNoleggi] = useState([]);
  const giorni = Array.from({ length: 7 }, (_, i) => addDays(inizio, i));

  const load = useCallback(() => {
    const da = `${ymd(inizio)}T00:00`, a = `${ymd(addDays(inizio, 7))}T00:00`;
    api.get("/noleggi", { params: { da, a } }).then((r) => setNoleggi(r.data.filter((n) => n.stato !== "annullato"))).catch((e) => toast.error(formatError(e)));
  }, [inizio]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/bici_noleggio").then((r) => setFlotta(r.data)); }, []);

  const liberiOggi = (g) => flotta.filter((b) => b.stato_flotta === "disponibile" && !noleggi.some((n) => n.bici_ids.includes(b.id) && n.inizio < `${ymd(g)}T23:59` && n.fine > `${ymd(g)}T00:00`)).length;

  return (
    <div>
      <PageHeader overline="Noleggio" title="Calendario flotta">
        <Button variant="outline" size="icon" onClick={() => setInizio(addWeeks(inizio, -1))} data-testid="cal-prev-week"><ChevronLeft className="h-4 w-4" /></Button>
        <Button variant="outline" onClick={() => setInizio(startOfWeek(new Date(), { weekStartsOn: 1 }))} data-testid="cal-this-week">Questa settimana</Button>
        <Button variant="outline" size="icon" onClick={() => setInizio(addWeeks(inizio, 1))} data-testid="cal-next-week"><ChevronRight className="h-4 w-4" /></Button>
      </PageHeader>
      <p className="mb-3 text-sm font-semibold capitalize text-slate-700" data-testid="cal-week-title">
        {format(inizio, "d MMMM", { locale: it })} – {format(addDays(inizio, 6), "d MMMM yyyy", { locale: it })}
      </p>
      <div className="mb-4 flex flex-wrap gap-3 text-xs text-slate-600">
        {LEGENDA.map(([l, c]) => <span key={l} className="flex items-center gap-1.5"><span className={`h-3 w-3 rounded-sm ring-1 ${c}`} />{l}</span>)}
      </div>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[900px] table-fixed text-sm" data-testid="cal-table">
          <thead>
            <tr className="bg-slate-50">
              <th className="w-48 px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Bici</th>
              {giorni.map((g) => (
                <th key={ymd(g)} className={`px-2 py-2 text-left text-[11px] font-bold uppercase tracking-wider ${isToday(g) ? "text-orange-600" : "text-slate-500"}`}>
                  {format(g, "EEE d", { locale: it })}<span className="block font-mono text-[10px] font-normal normal-case text-slate-400" data-testid={`cal-liberi-${ymd(g)}`}>{liberiOggi(g)} libere</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {flotta.map((b) => (
              <tr key={b.id} data-testid={`cal-row-${b.id}`}>
                <td className="px-3 py-2 align-top"><span className="font-mono text-xs font-bold text-orange-600">{b.codice}</span><p className="truncate font-semibold text-slate-900">{b.marca} {b.modello}</p><p className="text-xs text-slate-400">{b.taglia}</p></td>
                {giorni.map((g) => (
                  <td key={ymd(g)} className={`px-1.5 py-2 align-top ${isToday(g) ? "bg-orange-50/40" : ""}`}>
                    <Cella bike={b} giorno={g} noleggi={noleggi} onApri={(id) => navigate(`/noleggi?apri=${id}`)} onNuovo={(bid, d) => navigate(`/noleggi?bici=${bid}&inizio=${d}T09:00`)} />
                  </td>
                ))}
              </tr>
            ))}
            {!flotta.length && <tr><td colSpan={8} className="py-16 text-center text-sm text-slate-500" data-testid="cal-empty">Nessuna bici nella flotta. Aggiungile da "Flotta noleggio".</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
